import type { TFunction } from 'i18next';
import { canonicalCategory } from '@/lib/providers/integration-state';
import type { IntegrationMode } from '@/lib/providers/types';

/**
 * Formatting shared by the whole Providers page. Every phrase is a translation
 * (the caller's `t`), never code.
 */

/** Localised "12s ago" / "3m ago" / "1h ago" from an ISO instant; '' when unparseable. */
export function relativeTime(iso: string, t: TFunction<'network'>): string {
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return '';
  const secs = Math.max(0, Math.floor((Date.now() - at) / 1000));
  if (secs < 60) return t('providers.time.secondsAgo', { n: secs });
  if (secs < 3600) return t('providers.time.minutesAgo', { n: Math.floor(secs / 60) });
  return t('providers.time.hoursAgo', { n: Math.floor(secs / 3600) });
}

/** `19/10 21:30` in the operator's locale; the raw value when unparseable. */
export function formatWhen(iso: string, locale: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at.toLocaleString(locale, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/**
 * `search · book · cancel`, in the operator's language; the wire token when the
 * copy has not learned it yet. A token containing `.` would be read by i18next
 * as a nested path, so it skips the map.
 */
export function capabilitiesText(t: TFunction<'network'>, capabilities: string[] | undefined): string {
  if (!capabilities?.length) return '—';
  return capabilities
    .map((c) => (c.includes('.') ? c : t(`providers.capabilityName.${c}`, { defaultValue: c })))
    .join(' · ');
}

/**
 * What a mode MEANS for one service — the mode chip's hover and the mode
 * dialog's warning. Real is not real money for every service (notifications:
 * real messages to passengers), so production names the effect per canonical
 * service; an unlisted service, or none given, reads "real calls to the
 * provider". Sandbox and synthetic keep their mode-wide sentence.
 */
export function modeWarning(t: TFunction<'network'>, mode: IntegrationMode, category = ''): string {
  if (mode !== 'production') return t(`providers.integrations.modeHint.${mode}`);
  const other = t('providers.mode.realEffect.other');
  const effect = category ? t(`providers.mode.realEffect.${canonicalCategory(category)}`, { defaultValue: other }) : other;
  return t('providers.mode.realHint', { effect });
}
